import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import { ConnectedFocusError } from "focus-formik-error";
import { useTranslation } from "react-i18next";
import {
  getTicketFees,
  getTicketingFeeSummary,
  numberWithCommas
} from "../../../../../components/utilities/UtilityFunctions";
import ModalLoadingFull from "../../../../../components/modals/ModalLoadingFull";
import HelpButton from "../../../../../components/utilities/HelpButton";
import {
  BCPAngleDown,
  BCPCircleCheck
} from "../../../../../components/icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../../../../global/environmentVariables";
import { useNavigate } from "react-router";
import EventCardPreview from "../../../../../components/layout/eventcard/EventCardPreview";
const IS_UY = getEnvironmentVariable("IS_UY") === "true";

export default function EventCreationRegistraion({
  getInitialValues,
  getValidationSchema,
  submitEvent,
  setEventValues,
  eventValues,
  hasSubAccess,
  theme,
  readOnly,
  hasTicketingAccess,
  eventId,
  event,
  step,
  setStep,
  EventStepper,
  circuits,
  gameStore,
  authContext,
  buildPreviewEvent
}) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.eventCreation"
  });
  const muiTheme = useTheme();
  const isMdUp = useMediaQuery(muiTheme.breakpoints.up("md"));
  const [initialValues, setInitialValues] = useState(null);
  const [ticketingFees, setTicketingfees] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feeProcessing, setFeeProcessing] = useState(false);

  const ticketFeeSummary = (values) => {
    return getTicketingFeeSummary(
      values?.numTickets || 0,
      values?.ticketPrice || 0,
      values?.playerPaysFees && ticketingFees?.FixedPlayer !== undefined
        ? ticketingFees?.FixedPlayer
        : ticketingFees?.Fixed,
      values?.playerPaysFees && ticketingFees?.PercentPlayer !== undefined
        ? ticketingFees?.PercentPlayer
        : ticketingFees?.Percent,
      !(values?.playerPaysFees || false)
    );
  };

  const getTicketingFees = async (_currency) => {
    setFeeProcessing(true);
    const queryParams = {
      currency: _currency?.toUpperCase()
    };

    if (gameStore?.id || event?.gameStore?.id) {
      queryParams.gameStoreId = gameStore?.id || event?.gameStore?.id;
    } else {
      queryParams.ownerId =
        event?.ownerId ||
        event?.owner?.id ||
        authContext?.authState?.identity?.userId;
    }

    if (event?.id || eventId) queryParams.eventId = event?.id || eventId;

    const _ticketingFees = await getTicketFees(queryParams);
    setTicketingfees(_ticketingFees);
    setFeeProcessing(false);
  };
  const navigate = useNavigate();
  const form = document.querySelector("form");
  useEffect(() => {
    if (initialValues) {
      let _currency = (initialValues?.ticketCurrency || "USD")?.toUpperCase();
      let _ticketingFees = initialValues?.ticketingFees;
      if (!_ticketingFees) {
        getTicketingFees(_currency);
      } else {
        setTicketingfees(_ticketingFees);
      }
    } else {
      let _initialValues = getInitialValues();
      setInitialValues(_initialValues);
    }
    setLoading(false);

    // eslint-disable-next-line
  }, [initialValues]);

  return (
    <Grid item xs={12} sx={{ pt: 2 }}>
      {loading ? (
        <ModalLoadingFull />
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={getValidationSchema()}
          onSubmit={submitEvent}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values,
            setFieldValue,
            setFieldTouched
          }) => (
            <form onSubmit={handleSubmit}>
              {/* Event Creation Progress Stepper */}
              <EventStepper
                step={step}
                setStep={setStep}
                event={event}
                circuits={circuits}
              />

              <ConnectedFocusError />

              <Grid
                container
                spacing={2}
                pt={1}
                onBlur={() => {
                  setEventValues(
                    eventValues ? { ...eventValues, ...values } : { ...values }
                  );
                }}
              >
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Divider
                    sx={{
                      mt: 1,
                      mb: 1
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h4" fontWeight="700">
                    {t("playerRegistration")}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <RadioGroup
                    defaultValue={!values.usingOnlineReg}
                    disabled={readOnly}
                  >
                    <FormControlLabel
                      checked={!values.usingOnlineReg}
                      control={<Radio />}
                      onChange={() => {
                        setFieldValue("usingOnlineReg", false);
                      }}
                      label={
                        <Typography className="MuiFormControlLabel-label">
                          {t("manuallyEnterRoster")}
                          <HelpButton title={t("manuallyEnterRosterHelp")} />
                        </Typography>
                      }
                      disabled={readOnly}
                    />
                    <FormControlLabel
                      checked={values.usingOnlineReg}
                      control={<Radio />}
                      onChange={() => {
                        setFieldValue("usingOnlineReg", true);
                        getTicketingFees(values?.ticketCurrency || "USD");
                      }}
                      label={
                        <Typography className="MuiFormControlLabel-label">
                          {t("useBCPOnlineRegistration")}
                          <HelpButton
                            title={t("useBCPOnlineRegistrationHelp")}
                          />
                        </Typography>
                      }
                      disabled={readOnly}
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Divider
                    sx={{
                      mt: 1,
                      mb: 1
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    label={
                      <Box
                        sx={{
                          lineHeight: "16px",
                          color: hasSubAccess
                            ? "text.primary"
                            : theme?.palette?.text?.disabled
                        }}
                      >
                        <Typography className="MuiFormControlLabel-label">
                          {t("privateEvent", {
                            appName: getEnvironmentVariable("APP_NAME")
                          })}
                        </Typography>
                        <Typography
                          className="MuiFormControlLabel-label"
                          variant="caption"
                        >
                          {t("privateEventDescription")}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      ml: 0,
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "calc(100% - 58px)"
                      }
                    }}
                    control={
                      <Switch
                        checked={values.privateEvent}
                        onChange={() => {
                          setFieldValue("privateEvent", !values?.privateEvent);
                        }}
                        disabled={hasSubAccess ? false : true || readOnly}
                      />
                    }
                    disabled={hasSubAccess ? false : true || readOnly}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider
                    sx={{
                      mt: 1,
                      mb: 1
                    }}
                  />
                </Grid>

                {values?.usingOnlineReg && (
                  <>
                    {values?.eventType !== "Teams Event" &&
                      values?.eventType !== "Doubles Event" && (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="h4" fontWeight="700">
                              {t("shippingDetails")}
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="boxy1">
                              {t("shippingDetailsQuestion")}
                            </Typography>

                            <RadioGroup
                              defaultValue={values?.shippingDetails?.requested}
                              sx={{ mt: 2 }}
                            >
                              <FormControlLabel
                                value={!values?.shippingDetails?.requested}
                                checked={!values?.shippingDetails?.requested}
                                control={<Radio />}
                                onClick={() => {
                                  setFieldValue(
                                    "shippingDetails.requested",
                                    false
                                  );
                                  setFieldValue(
                                    "shippingDetails.mandatory",
                                    false
                                  );
                                }}
                                label={
                                  <Box sx={{ lineHeight: "16px" }}>
                                    <Typography
                                      className="MuiFormControlLabel-label"
                                      variant="body1"
                                    >
                                      {t("no")}
                                    </Typography>
                                  </Box>
                                }
                                disabled={readOnly}
                              />
                              <FormControlLabel
                                value={values?.shippingDetails?.requested}
                                checked={values?.shippingDetails?.requested}
                                control={<Radio />}
                                onClick={() => {
                                  setFieldValue(
                                    "shippingDetails.requested",
                                    true
                                  );
                                  setFieldValue(
                                    "shippingDetails.mandatory",
                                    true
                                  );
                                }}
                                label={
                                  <Typography className="MuiFormControlLabel-label">
                                    {t("yes")}
                                  </Typography>
                                }
                                disabled={readOnly}
                              />
                            </RadioGroup>

                            {values?.shippingDetails?.requested && (
                              <>
                                <Typography
                                  className="MuiFormControlLabel-label"
                                  sx={{ mt: 2 }}
                                >
                                  {t("shippingDescriptionOptional")}
                                  <HelpButton
                                    title={t("shippingDescriptionHelp")}
                                  />
                                </Typography>
                                <TextField
                                  name="shippingDetails.description"
                                  value={values.shippingDetails.description}
                                  variant="outlined"
                                  fullWidth
                                  type="text"
                                  placeholder={t(
                                    "shippingDescriptionPlaceholder"
                                  )}
                                  onChange={handleChange}
                                  disabled={readOnly}
                                  multiline
                                  rows={2}
                                />
                              </>
                            )}
                          </Grid>

                          <Grid item xs={12}>
                            <Divider
                              sx={{
                                mt: 1,
                                mb: 1
                              }}
                            />
                          </Grid>
                        </>
                      )}

                    <Grid item xs={12}>
                      <Typography variant="h4" fontWeight="700">
                        {t("ticketDetails")}
                      </Typography>
                    </Grid>

                    {/* TODO: Add ticketing options here */}
                    <Grid item xs={12}>
                      <TextField
                        name="numTickets"
                        label={t("numberOfTickets")}
                        value={values.numTickets}
                        variant="outlined"
                        fullWidth
                        required
                        type="number"
                        onBlur={handleChange}
                        onChange={handleChange}
                        error={Boolean(
                          touched.numTickets && values.numTickets === ""
                        )}
                        helperText={
                          touched.numTickets && values.numTickets === ""
                            ? t("required")
                            : null
                        }
                        disabled={readOnly}
                      />
                    </Grid>

                    {!IS_UY && hasTicketingAccess && (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            name="ticketPrice"
                            label={t("price")}
                            value={values.ticketPrice}
                            variant="outlined"
                            fullWidth
                            required
                            type="number"
                            onBlur={(e) => {
                              setFieldValue("pricingDict", {
                                [values.currency || "usd"]: Math.round(
                                  e.target.value * 100
                                )
                              });
                              handleChange(e);
                            }}
                            onChange={(e) => {
                              setFieldValue("pricingDict", {
                                [values.currency || "usd"]: Math.round(
                                  e.target.value * 100
                                )
                              });
                              handleChange(e);
                            }}
                            error={Boolean(
                              touched.ticketPrice && values.ticketPrice === ""
                            )}
                            helperText={
                              touched.ticketPrice && values.ticketPrice === ""
                                ? t("required")
                                : null
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  {values.currency?.toUpperCase()}
                                </InputAdornment>
                              )
                            }}
                            disabled={readOnly}
                          />
                          {values?.ticketPrice === 0 && (
                            <Typography
                              variant="caption"
                              sx={{ textAlign: "center" }}
                            >
                              {t("freeEventNote")}
                            </Typography>
                          )}
                        </Grid>

                        {values?.ticketPrice > 0 && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="h4">
                                {feeProcessing ? (
                                  <>
                                    {t("whoCoversFee", {
                                      fee: "",
                                      currency: ""
                                    }).replace("  ", " ")}
                                    <CircularProgress size={12} />
                                  </>
                                ) : (
                                  t("whoCoversFee", {
                                    fee: numberWithCommas(
                                      (
                                        ticketFeeSummary(values).fee || 0
                                      ).toFixed(2)
                                    ),
                                    currency: values.currency?.toUpperCase()
                                  })
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <ButtonGroup fullWidth>
                                <Button
                                  variant={
                                    values?.playerPaysFees
                                      ? "outlined"
                                      : "contained"
                                  }
                                  onClick={() => {
                                    setFieldValue("playerPaysFees", false);
                                    getTicketingFees(
                                      values?.ticketCurrency || "USD"
                                    );
                                  }}
                                  startIcon={
                                    !values?.playerPaysFees && (
                                      <BCPCircleCheck />
                                    )
                                  }
                                >
                                  {t("organizer")}
                                </Button>
                                <Button
                                  variant={
                                    values?.playerPaysFees
                                      ? "contained"
                                      : "outlined"
                                  }
                                  onClick={() => {
                                    setFieldValue("playerPaysFees", true);
                                    getTicketingFees(
                                      values?.ticketCurrency || "USD"
                                    );
                                  }}
                                  startIcon={
                                    values?.playerPaysFees && <BCPCircleCheck />
                                  }
                                >
                                  {t("player")}
                                </Button>
                              </ButtonGroup>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption">
                                {t("transactionFeeDescription")}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sx={{ minHeight: "90px" }}>
                              {feeProcessing ? (
                                <></>
                              ) : (
                                <>
                                  <Typography
                                    variant="body2"
                                    sx={{ textDecoration: "underline" }}
                                    gutterBottom
                                  >
                                    {t("perTicket")}
                                  </Typography>
                                  <Typography variant="body1">
                                    {t("organizerWillMake")}{" "}
                                    {numberWithCommas(
                                      (
                                        (ticketFeeSummary(values).amount || 0) -
                                        (ticketFeeSummary(values).fee || 0)
                                      ).toFixed(2)
                                    )}{" "}
                                    {values.currency?.toUpperCase()}
                                  </Typography>
                                  <Typography variant="body1">
                                    {t("playerWillPayTotal")}{" "}
                                    {numberWithCommas(
                                      (
                                        ticketFeeSummary(values).amount || 0
                                      ).toFixed(2)
                                    )}{" "}
                                    {values.currency?.toUpperCase()}
                                  </Typography>
                                </>
                              )}
                            </Grid>

                            {gameStore?.chargeType === "square" && (
                              <Grid item xs={12}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "warning.main" }}
                                >
                                  {t("squareRefundNotice")}
                                </Typography>
                              </Grid>
                            )}

                            <Grid item xs={12}>
                              <TextField
                                name="refundDate"
                                label={t("refundDate")}
                                variant="outlined"
                                fullWidth
                                onBlur={handleChange}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.refundDate && values.refundDate === ""
                                )}
                                helperText={
                                  touched.refundDate && values.refundDate === ""
                                    ? t("required")
                                    : null
                                }
                                disabled={
                                  readOnly || gameStore?.chargeType === "square"
                                }
                                type="datetime-local"
                                defaultValue={values.refundDate}
                                InputLabelProps={{
                                  shrink: true
                                }}
                                sx={{
                                  colorScheme:
                                    theme?.palette?.mode === "dark"
                                      ? "dark"
                                      : null
                                }}
                              />
                            </Grid>
                          </>
                        )}
                      </>
                    )}

                    <Grid item xs={12}>
                      <Divider
                        sx={{
                          mt: 1,
                          mb: 1
                        }}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Accordion
                    sx={{
                      width: "100%",
                      backgroundColor:
                        theme?.palette?.mode === "dark"
                          ? "background.secondary"
                          : "background.primary"
                    }}
                  >
                    <AccordionSummary expandIcon={<BCPAngleDown />}>
                      <Typography variant="body1">
                        {t("playerCheckIn")}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <FormControl>
                        <RadioGroup
                          defaultValue={values?.disableCheckin}
                          name="radio-buttons-group"
                        >
                          <FormControlLabel
                            value={!values?.disableCheckin}
                            checked={!values?.disableCheckin}
                            control={<Radio />}
                            onChange={() => {
                              setFieldValue("disableCheckin", false);
                            }}
                            label={
                              <Box sx={{ lineHeight: "16px" }}>
                                <Typography
                                  className="MuiFormControlLabel-label"
                                  variant="body1"
                                >
                                  {t("playersCanCheckInThemselves")}
                                </Typography>
                                <Typography variant="caption">
                                  {t("checkInOpensDescription")}
                                </Typography>
                              </Box>
                            }
                            disabled={readOnly}
                          />
                          <FormControlLabel
                            value={values?.disableCheckin}
                            checked={values?.disableCheckin}
                            control={<Radio />}
                            onChange={() => {
                              setFieldValue("disableCheckin", true);
                            }}
                            label={
                              <Typography className="MuiFormControlLabel-label">
                                {t("playersMustCheckInInPerson")}
                              </Typography>
                            }
                            disabled={readOnly}
                            sx={{ mt: 1 }}
                          />
                        </RadioGroup>
                      </FormControl>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      if (
                        !event?.id &&
                        (circuits?.length || event?.circuits?.length)
                      ) {
                        setStep(5);
                        navigate(
                          `${window.location.pathname}?active_tab=circuits`,
                          {
                            replace: false
                          }
                        );
                        window.scrollTo(0, 0);
                      } else {
                        form.requestSubmit();
                      }
                    }}
                  >
                    {eventId
                      ? t("saveChanges")
                      : circuits?.length > 0
                        ? t("nextCircuits")
                        : t("createEvent")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>

                {isMdUp && (
                  <Grid
                    item
                    md={4}
                    sx={{ display: { xs: "none", md: "block" } }}
                  >
                    <Box
                      sx={{
                        position: "sticky",
                        top: 145,
                        maxHeight: "calc(100vh - 120px)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center"
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", mb: 1, display: "block" }}
                      >
                        {t("eventCardPreview") || "Event Card Preview"}
                      </Typography>
                      <Box sx={{ flex: "0 1 auto", minHeight: 0 }}>
                        <EventCardPreview
                          event={buildPreviewEvent(values)}
                          preview={true}
                        />
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </form>
          )}
        </Formik>
      )}

      {feeProcessing && <ModalLoadingFull />}
    </Grid>
  );
}
